import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiX } from "react-icons/hi";
import { toast } from "react-toastify";
import TextEditor from "components/common/TextEditor/TextEditor";
import { saveFeedEvent } from "./feedEvents.api";
import {
  FEED_EVENT_IMAGE_ACCEPT,
  FEED_EVENT_IMAGE_MAX_BYTES,
  eventImageSrc,
  feedEventErrorMessage,
  hasFormattedText,
} from "./feedEvents.utils";

const emptyForm = () => ({
  uuid: null,
  title: "",
  description: "",
  imageFile: null,
  existingImage: "",
});

const EventFormModal = ({ open, event, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm());
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setError("");
    setForm({
      uuid: event?.uuid || null,
      title: event?.title || "",
      description: event?.description || "",
      imageFile: null,
      existingImage: event?.image || "",
    });

    return undefined;
  }, [open, event]);

  useEffect(() => {
    if (!form.imageFile) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(form.imageFile);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [form.imageFile]);

  const pictureSrc = previewUrl || eventImageSrc(form.existingImage);

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    setError("");

    const title = form.title.trim();
    if (!title) {
      setError("Title is required");
      return;
    }

    if (!hasFormattedText(form.description)) {
      setError("Formatted text is required");
      return;
    }

    if (!form.uuid && !form.imageFile) {
      setError("Picture is required");
      return;
    }

    if (form.imageFile && form.imageFile.size > FEED_EVENT_IMAGE_MAX_BYTES) {
      setError("Picture must be 5MB or smaller");
      return;
    }

    setSaving(true);

    try {
      await saveFeedEvent({
        uuid: form.uuid,
        title,
        description: form.description,
        imageFile: form.imageFile,
      });
      toast.success(form.uuid ? "Event updated" : "Event posted");
      onSaved();
      onClose();
    } catch (err) {
      setError(feedEventErrorMessage(err, "Failed to save event"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[10002]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/50" />
        </Transition.Child>

        <div className="fixed inset-0 z-[10002] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel className="w-full max-w-2xl overflow-visible rounded-xl bg-white p-6 shadow-xl">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {form.uuid ? "Edit event" : "Add event"}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close"
                  >
                    <HiX className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="feed-event-title"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Title
                    </label>
                    <input
                      id="feed-event-title"
                      type="text"
                      value={form.title}
                      maxLength={1000}
                      onChange={(changeEvent) =>
                        setForm((current) => ({
                          ...current,
                          title: changeEvent.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <span className="mb-1 block text-sm font-medium text-gray-700">
                      Formatted text
                    </span>
                    <TextEditor
                      text={form.description}
                      setText={(description) =>
                        setForm((current) => ({ ...current, description }))
                      }
                      minHeight={160}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="feed-event-image"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Picture
                    </label>
                    <input
                      id="feed-event-image"
                      type="file"
                      accept={FEED_EVENT_IMAGE_ACCEPT}
                      onChange={(changeEvent) => {
                        const file = changeEvent.target.files?.[0] || null;
                        setForm((current) => ({ ...current, imageFile: file }));
                      }}
                      className="block w-full text-sm text-gray-700"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      JPG, PNG, GIF, or WEBP. 5MB maximum.
                    </p>
                    {pictureSrc ? (
                      <img
                        src={pictureSrc}
                        alt=""
                        className="mt-3 max-h-48 w-full rounded-lg object-cover"
                      />
                    ) : null}
                  </div>

                  {error ? (
                    <p className="text-sm text-red-600" role="alert">
                      {error}
                    </p>
                  ) : null}

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={saving}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? "Saving…" : form.uuid ? "Save changes" : "Post event"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EventFormModal;
